import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { getItineraryUniqueKey } from 'lib/cruises/cruiseUtils'
import CruiseAccordion from './CruiseAccordion'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SquareCheckboxInput from 'components/Luxkit/Checkbox/SquareCheckboxInput'
import Group from 'components/utils/Group'
import { arrayToObject } from 'lib/array/arrayUtils'
import ItinerarySideLine from './ItinerarySideLine'
import useCruiseUniqueItineraryList from 'hooks/Cruise/useCruiseUniqueItineraryList'

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ItineraryAccordionArea = styled.div`
  flex-grow: 1;
`

interface Props {
  itinerary: Array<App.CruiseItineraryItem>
  title?: string
  portNameSelected?: string | null
  itineraryKeySelected?: string | null
  onSelectPort?: (port?: App.CruisePort, itineraryKey?: string) => void
}

export type UniqueItineraryItem = Record<string, {
  item: App.CruiseItineraryItem,
  addPortCircle: boolean
  portName: string
  portNumber: number
  isStart: boolean
  isEnd: boolean
  itineraryKey: string
  isSelected: boolean
}>

function CruiseItinerary(props: Props) {
  const { itinerary, title, onSelectPort, portNameSelected, itineraryKeySelected } = props
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>(arrayToObject(itinerary, item => `${item.startDay}`, () => false))
  const [showOnlyPorts, setShowOnlyPorts] = useState<boolean>(false)

  // Remove duplicate days at sea
  const uniqueItineraryList = useCruiseUniqueItineraryList({
    itinerary,
    showOnlyPorts,
    portNameSelected,
    itineraryKeySelected,
  })

  const toggleAccordion = useCallback((key: string, item: App.CruiseItineraryItem) => {
    const newExpandedValue = !expandedAccordions[key]
    setExpandedAccordions({ ...expandedAccordions, [key]: newExpandedValue })

    if (onSelectPort) {
      onSelectPort(item.port ?? undefined, key)
    }
  }, [expandedAccordions, onSelectPort])

  const toggleAllAccordion = useCallback(() => {
    const hasClosedItems = Object.values(expandedAccordions).some(item => !item)

    if (hasClosedItems) {
      setExpandedAccordions(arrayToObject(itinerary, item => getItineraryUniqueKey(item), () => true))
    } else {
      setExpandedAccordions(arrayToObject(itinerary, item => getItineraryUniqueKey(item), () => false))
    }
  }, [expandedAccordions, itinerary])

  return <Group direction="vertical" gap={20}>
    <Group direction="vertical" gap={4}>
      {!!title && <TitleBar>
        <Heading variant="heading3" align="center">{title}</Heading>
      </TitleBar>}

      <BodyText variant="medium">
        Please note that all dates and times are displayed in local time.
      </BodyText>
    </Group>

    <Group direction="horizontal" gap={20}>
      <SquareCheckboxInput
        variant="large"
        onChange={() => setShowOnlyPorts(!showOnlyPorts)}
        checked={showOnlyPorts}
      >
        Show ports only
      </SquareCheckboxInput>

      <SquareCheckboxInput
        variant="large"
        onChange={toggleAllAccordion}
        checked={Object.values(expandedAccordions).every(item => item)}
      >
        Expand all
      </SquareCheckboxInput>
    </Group>

    <div>
      {uniqueItineraryList.map(({ item, portNumber, portName, isStart, isEnd, addPortCircle, itineraryKey, isSelected }) => {
        return <Group
          key={itineraryKey}
          direction="horizontal"
          gap={16}
        >
          <ItinerarySideLine
            portName={portName}
            portNumber={portNumber}
            isStart={isStart}
            isEnd={isEnd}
            addPortCircle={addPortCircle}
          />
          <ItineraryAccordionArea>
            <CruiseAccordion
              itineraryItem={item}
              isSelected={isSelected}
              open={expandedAccordions[itineraryKey] && !!item.description}
              toggleAccordion={toggleAccordion}
            />
          </ItineraryAccordionArea>
        </Group>
      })}
    </div>
  </Group>
}

export default CruiseItinerary
